import {
  BehaviorSubject,
  MonoTypeOperatorFunction,
  OperatorFunction,
  SchedulerLike,
  Subject,
  Subscription,
  merge,
  of,
  pipe,
  timer,
} from 'rxjs';
import {
  filter,
  map,
  mergeMap,
  share,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  UnknownAction,
  UnknownActionCreator,
  UnknownActionCreatorWithPayload,
} from '../internal/types';
import {
  ActionWithPayload,
  ActionWithoutPayload,
  NamespacedAction,
} from '../types/Action';
import { NamespacedActionCreator } from '../types/ActionCreator';

interface OfType {
  /**
   * Stream operator to filter specific namespaced actions that have overlapping payload
   *
   * ```
   * action$.pipe(
   *   ofType(anAction, anotherAction),
   *   tap(action => {
   *     // `action` is now guaranteed to be of the type overlap between
   *     // anAction and anotherAction
   *   })
   * )
   * ```
   *
   * @param targetTypes The types to filter for
   */
  <Payload>(
    ...targetTypes: NamespacedActionCreator<Payload>[]
  ): OperatorFunction<UnknownAction, NamespacedAction<Payload>>;

  /**
   * Stream operator to filter specific actions that have overlapping payload
   *
   * ```
   * action$.pipe(
   *   ofType(anAction, anotherAction),
   *   tap(action => {
   *     // `action` is now guaranteed to be of the type overlap between
   *     // anAction and anotherAction
   *   })
   * )
   * ```
   *
   * @param targetTypes The types to filter for
   */
  <Payload>(
    ...targetTypes: UnknownActionCreatorWithPayload<Payload>[]
  ): OperatorFunction<UnknownAction, ActionWithPayload<Payload>>;

  /**
   * Stream operator to filter specific actions that have non-overlapping payload
   *
   * You might hit this overload even when there is overlap, in those cases, you
   * can specify the overlap manually with the generic argument.
   *
   * ```
   * action$.pipe(
   *   ofType(anAction, anotherAction),
   *   tap(action => {
   *     // `action` is now of type Action<{}>, meaning the payload field is
   *     // available, but not nothing in it is.
   *   })
   * )
   * ```
   *
   * @param targetTypes The types to filter for
   */
  (
    ...targetTypes: UnknownActionCreatorWithPayload<unknown>[]
  ): OperatorFunction<UnknownAction, ActionWithPayload<unknown>>;

  /**
   * Stream operator to filter specific actions that does not have payloads
   *
   * ```
   * action$.pipe(
   *   ofType(anAction, anotherAction),
   *   tap(action => {
   *     // `action` is now of type ActionWithoutPayload
   *   })
   * )
   * ```
   *
   * @param targetTypes The types to filter for
   */
  (
    ...targetTypes: UnknownActionCreator[]
  ): OperatorFunction<UnknownAction, ActionWithoutPayload>;
}
export const ofType: OfType = ((
  ...targetTypes: UnknownActionCreator[]
): OperatorFunction<UnknownAction, UnknownAction> => {
  const types = new Set(targetTypes.map(({ type }) => type));

  return pipe(filter((action: UnknownAction) => types.has(action.type)));
}) as any; // Implementation is untyped

/**
 * Stream operator to extract the payload from an action
 *
 * @template `Payload` - The type of the payload, can be inferred if the stream
 *                       is typed, or explicitly set if the stream has `any`
 *                       type or the payload type is `any`
 */
export const extractPayload = <Payload>(): OperatorFunction<
  ActionWithPayload<Payload>,
  Payload
> => map(action => action.payload);

/**
 * Stream operator that excludes actions with the wrong namespace
 *
 * @param namespace The namespace to filter for
 */
export const withNamespace = (
  targetNamespace: string
): MonoTypeOperatorFunction<UnknownAction> =>
  filter(
    ({ meta: { namespace } }) =>
      namespace === undefined || namespace === targetNamespace
  );

/**
 * Stream operator that excludes actions with the given namespace
 *
 * If no namespace is provided, it will exclude all actions that has any
 * namespace
 *
 * @param excludedNamespace Optionally a specific namespace to exclude
 */
export const withoutNamespace = (
  excludedNamespace?: string
): MonoTypeOperatorFunction<UnknownAction> =>
  filter(({ meta: { namespace } }) =>
    excludedNamespace === undefined
      ? namespace === undefined
      : namespace !== excludedNamespace
  );

/**
 * Stream operator that carries the initial payload alongside the results
 * from the operator parameter. Using this makes the observable/stream hot.
 *
 * ```
 * routine(
 *   extractPayload(),
 *   carry(map(payload => payload.foo))
 *   tap(([payload, foo]) => {
 *     // `payload.foo === foo` equals `true`
 *   })
 * )
 * ```
 *
 * @param operator The operator to execute
 * @deprecated Prefer to refactor your data flow so `carry` isn't needed. See also the coding guidelines.
 */
export const carry =
  <Carried, Emitted>(
    operator: OperatorFunction<Carried, Emitted>
  ): OperatorFunction<Carried, [Carried, Emitted]> =>
  observable => {
    const hot = observable.pipe(share());
    return hot.pipe(
      operator,
      withLatestFrom(hot, (emitted, carried) => [carried, emitted])
    );
  };

/**
 * A utility operator for using pipes which need a value to be present
 * throughout the pipe.
 *
 * The main use for this operator is to provide context to `catchError`. `carry`
 * should be preferred where possible.
 *
 * **NB** Use with caution. This is very inefficient and should only be used for
 * providing context to `catchError`.
 *
 * Example:
 * ```ts
 * routine(
 *   ofType(myAction),
 *   apply(action => pipe(
 *     map(action => ...),
 *     tap(mapped => ...),
 *     catchError(() => {
 *       console.log('Error from action:', action);
 *     })
 *   ))
 * )
 * ```
 *
 * @param operator A function that returns an operator function
 */
export const apply = <P, R>(
  operator: (payload: P) => OperatorFunction<P, R>
): OperatorFunction<P, R> =>
  mergeMap(payload => of(payload).pipe(operator(payload)));

/**
 * Stream operator that spreads an array of values into a function call
 *
 * @example
 * ```ts
 * combineLatest([first, second, third]).pipe(
 *   spreadMap(mapToSomethingElse)
 * )
 * const mapToSomethingElse = (a, b, c) => ...;
 * ```
 */
export const spreadMap = <T extends unknown[], R>(project: (...args: T) => R) =>
  map((args: T) => project(...args));

/**
 * Stream operator that spreads an array of values into a side effect function call
 *
 * @example
 * ```ts
 * combineLatest([first, second, third]).pipe(
 *   spreadTap(performSideEffect)
 * )
 * const performSideEffect = (a, b, c) => { ... };
 * ```
 */
export const spreadTap = <T extends unknown[]>(
  sideEffect: (...args: T) => void
) => tap((args: T) => sideEffect(...args));

const notYetEmitted: unique symbol = Symbol('not yet emitted');

/**
 * Share a stream with Stale-While-Revalidate behavior.
 *
 * When subscribed to, it will immediately emit the most recent value, then
 * continue to emit new values as they come in. When new subscriptions trigger
 * a revalidation, the revalidated values will also be emitted to existing
 * subscribers.
 *
 * If there are no active subscriptions to the underlying `source$`
 * (because it completed, or all subscribers had unsubscribed), a new
 * subscription will be made in order to revalidate.
 */
export const shareSWR =
  <T>(staleTime = 0, scheduler?: SchedulerLike): MonoTypeOperatorFunction<T> =>
  source$ => {
    const value$ = new BehaviorSubject<T | typeof notYetEmitted>(notYetEmitted);
    const error$ = new Subject<unknown>();

    let subscription: Subscription | null = null;
    let subscribers = 0;

    return merge(
      value$.pipe(filter((value): value is T => value !== notYetEmitted)),
      error$.pipe(
        map(err => {
          throw err;
        })
      )
    ).pipe(
      tap({
        subscribe() {
          subscribers++;

          if (!subscription) {
            subscription = source$.subscribe({
              next: value => value$.next(value),
              error: error => error$.next(error),
            });
            subscription.add(() => {
              if (value$.value === notYetEmitted || staleTime === 0) {
                subscription = null;
              } else {
                timer(staleTime, scheduler).subscribe(() => {
                  subscription = null;
                });
              }
            });
          }
        },
        finalize() {
          subscribers--;

          if (subscribers === 0 && subscription) {
            subscription.unsubscribe();
          }
        },
      })
    );
  };
